import { Size2MixAccordion, Size2MixExpandCollapseBar, Size2MixSection, Size2MixSimpleTable, useSectionsToggle } from '@shared/size2mix-shell';
import type { TopologyData } from './types';
import './synthesis.css';

const SECTIONS = ['departures', 'pumps', 'substations', 'pressure', 'limiters'] as const;
const dash = (v: number | null) => (v == null ? '-' : String(v));

export default function SynthesisTab({ data }: { data: TopologyData }) {
    const { bind, expandAll, collapseAll } = useSectionsToggle(SECTIONS);

    const empty = (labels: string[]) => (
        <div className="topology-synthesis-empty">
            <Size2MixSimpleTable columns={labels.map((label, i) => ({ key: `c${i}`, label }))} rows={[]} />
        </div>
    );

    return (
        <div className="topology-synthesis-compact">
        <Size2MixAccordion>
            <Size2MixExpandCollapseBar onExpandAll={expandAll} onCollapseAll={collapseAll} />
            <Size2MixSection title="Départs réseau" {...bind('departures')}>
                <Size2MixSimpleTable
                    columns={[
                        { key: 'site', label: 'Site de production' },
                        { key: 'departure', label: 'Départ' },
                        { key: 'maxMassFlow', label: 'Débit massique maximal (T/h)', render: (r) => dash(r.maxMassFlow) },
                        { key: 'tAtTextMin', label: 'T° départ à T°ext min (°C)', render: (r) => dash(r.tAtTextMin) },
                        { key: 'tAtTextInter', label: 'T° départ à T°ext inter (°C)', render: (r) => dash(r.tAtTextInter) },
                        { key: 'tAtTextMax', label: 'T° départ à T°ext max (°C)', render: (r) => dash(r.tAtTextMax) },
                        { key: 'pumpGroup', label: 'Groupe de pompes' },
                    ]}
                    rows={data.departures}
                    rowKey={(r) => r.departure}
                />
            </Size2MixSection>
            <Size2MixSection title="Pompes de reprise sur le réseau" {...bind('pumps')}>
                {empty(['Pompe', 'Type', 'Vitesse de rotation minimale (tr/min)', 'Vitesse de rotation nominale (tr/min)', 'Débit volumique (m³/h)', 'Delta de pression (bar)', 'Rendement'])}
            </Size2MixSection>
            <Size2MixSection title="Sous-stations d'échange" {...bind('substations')}>
                {empty([
                    "Sous-station d'échange",
                    'Température de sortie à température extérieure minimale (°C)',
                    'Température de sortie à température extérieure intermédiaire (°C)',
                    'Température de sortie à température extérieure maximale (°C)',
                    'Pincement',
                    'Puissance nominale (kW)',
                    'Perte de pression nominale (bar)',
                    'DeltaT nominal (Δ°C)',
                ])}
            </Size2MixSection>
            <Size2MixSection title="Maintiens de pression" {...bind('pressure')}>
                {empty(['Maintaining pressure devices', 'État de pression par défaut (bar)'])}
            </Size2MixSection>
            <Size2MixSection title="Limiteurs" {...bind('limiters')}>
                <Size2MixSimpleTable
                    columns={[
                        { key: 'name', label: 'Limiteur' },
                        { key: 'identifier', label: 'Identifiant' },
                        { key: 'minPressure', label: 'Pression minimale (bar)', render: (r) => dash(r.minPressure) },
                        { key: 'maxPressure', label: 'Pression maximale (bar)', render: (r) => dash(r.maxPressure) },
                        { key: 'minTemperature', label: 'Temperature minimale (°C)', render: (r) => dash(r.minTemperature) },
                        { key: 'maxTemperature', label: 'Temperature maximale (°C)', render: (r) => dash(r.maxTemperature) },
                    ]}
                    rows={data.limiters}
                    rowKey={(r) => r.identifier}
                />
            </Size2MixSection>
        </Size2MixAccordion>
        </div>
    );
}
